import * as React from 'react';
import { useRouter } from 'next/router';
import { environment } from '../../environment/environment';

const isBrowser = typeof window !== 'undefined';

const PIXEL_SCRIPT_ID = 'facebook-pixel-script';

/**
 * Higher Order Component for Meta Pixel tracking
 * @param pixelId - The Meta pixel ID
 */
export const withMetaPixelTracking =
  ({ pixelId }) =>
  (WrappedComponent: React.ComponentType<any>) =>
  (props: any) => {
    const router = useRouter();
    const pixelLoadedRef = React.useRef(false);
    const validToInjectScript = environment.REACT_APP_ENV === 'PROD';

    React.useEffect(() => {
      if (!router.isReady || !validToInjectScript) return;

      // Function to load the TikTok pixel script
      const loadPixelScript = () => {
        if (pixelLoadedRef.current || !isBrowser) return;

        // Check if script already exists
        if (document.getElementById(PIXEL_SCRIPT_ID)) {
          pixelLoadedRef.current = true;
          return;
        }

        // Create the TikTok pixel script
        const pixelScript = `
<!-- Meta Pixel Code -->
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${pixelId}');
fbq('track', 'PageView');
`;
        const pixelNoScript = `
<img height="1" width="1" style="display:none"
src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"
/>
<!-- End Meta Pixel Code -->
        `;

        const script = document.createElement('script');
        script.id = PIXEL_SCRIPT_ID;
        script.type = 'text/javascript';
        script.text = pixelScript;
        script.async = true;
        document.head.appendChild(script);

        const noscript = document.createElement('noscript');
        noscript.innerHTML = pixelNoScript;
        document.head.appendChild(noscript);

        pixelLoadedRef.current = true;
      };

      // Function to handle page view tracking

      // Main handler for route changes
      const handleRouteChange = () => {
        loadPixelScript();
      };

      // Initial tracking on component mount
      handleRouteChange();

      // Setup listener for route changes
      router.events.on('routeChangeComplete', handleRouteChange);

      // Cleanup listener on unmount
      return () => {
        router.events.off('routeChangeComplete', handleRouteChange);
      };
    }, [router.isReady, router.events, validToInjectScript]);

    return <WrappedComponent {...props} />;
  };
